import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getApiUrl, getCsrfToken } from "../../../utils/apiHelper";
import StatusResultModal from "../../StatusResultModal";
import ConfirmModal from "../../ConfirmModal";
import { profileTypeLabels } from "../../../utils/categoryLabels";
import { getReadinessLevel } from "../../../utils/readinessUtils";

const parseOverallReadiness = (raw) => {
  const value = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
};

const getProfileType = (report) =>
  report?.profile_type || report?.profile_details_summary?.profile_type || "";

const PROFILE_TYPE_FILTER_OPTIONS = Object.keys(profileTypeLabels).sort((a, b) => {
  if (a === "prompts_docs_llm") return -1;
  if (b === "prompts_docs_llm") return 1;
  return a.localeCompare(b);
});

const normalizeConsolidatedReport = (result) => ({
  ...result,
  overall_readiness: parseOverallReadiness(result.overall_readiness),
  is_consolidated: true,
});

const formatDate = (timestamp) => new Date(timestamp).toLocaleString();

const calculateOverallScore = (reportData) =>
  Math.round(parseOverallReadiness(reportData?.overall_readiness));

const getProfileTypeLabel = (report) => {
  const profileType = getProfileType(report);
  return profileTypeLabels[profileType] || profileType || "";
};

const sortReports = (a, b, sortBy, sortAsc) => {
  const dir = sortAsc ? 1 : -1;
  if (sortBy === "id") return ((Number(a.id) || 0) - (Number(b.id) || 0)) * dir;
  if (sortBy === "created_at") {
    return (
      ((a.created_at ? new Date(a.created_at).getTime() : 0) -
        (b.created_at ? new Date(b.created_at).getTime() : 0)) * dir
    );
  }
  if (sortBy === "overall_score") {
    return (calculateOverallScore(a) - calculateOverallScore(b)) * dir;
  }
  if (sortBy === "profile_type") {
    return String(getProfileTypeLabel(a)).localeCompare(String(getProfileTypeLabel(b))) * dir;
  }
  return String(a[sortBy] || "").localeCompare(String(b[sortBy] || "")) * dir;
};

export default function ConsolidatedReportHistory({
  refreshNonce = 0,
  onLoadingChange,
  onDetailLoadingChange,
  onReportLoaded,
  detailLoading = false,
  resetFilterNonce = 0,
}) {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_page" });
  const [consolidatedHistory, setConsolidatedHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySortBy, setHistorySortBy] = useState("id");
  const [historySortAsc, setHistorySortAsc] = useState(false);
  const [consolidatedSearchQuery, setConsolidatedSearchQuery] = useState("");
  const [consolidatedProfileTypeFilter, setConsolidatedProfileTypeFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    if (!resetFilterNonce) return;
    setConsolidatedProfileTypeFilter("");
    setConsolidatedSearchQuery("");
  }, [resetFilterNonce]);

  useEffect(() => {
    let cancelled = false;

    const fetchConsolidatedHistory = async () => {
      try {
        setLoadingHistory(true);
        onLoadingChange?.(true);

        const response = await fetch(
          getApiUrl("/api/internal/readiness-score/list-consolidated-summary/"),
          {
            method: "GET",
            credentials: "include",
            redirect: "follow",
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!Array.isArray(result)) {
          throw new Error("API response is not an array");
        }

        if (!cancelled) setConsolidatedHistory(result);
      } catch (err) {
        console.error("Error fetching consolidated report history:", err);
        if (!cancelled) setConsolidatedHistory([]);
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
          onLoadingChange?.(false);
        }
      }
    };

    fetchConsolidatedHistory();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce, onLoadingChange]);

  const handleConsolidatedRowClick = async (report) => {
    try {
      onDetailLoadingChange?.(true);

      const response = await fetch(
        getApiUrl(`/api/internal/readiness-score/getConsolidatedReportById/${report.id}/`),
        {
          method: "GET",
          credentials: "include",
          redirect: "follow",
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(t("reportNotFound"));
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || t("failedToLoadReport"));
      }

      const detailedReport = await response.json();
      onReportLoaded?.(normalizeConsolidatedReport(detailedReport));
    } catch (e) {
      console.error("Error fetching consolidated report by id:", e);
      setStatusModal({
        open: true,
        type: "error",
        message: e.message || t("failedToLoadReport"),
      });
    } finally {
      onDetailLoadingChange?.(false);
    }
  };

  const requestDeleteReport = (report, e) => {
    e?.stopPropagation();
    if (deletingId || detailLoading) return;
    setPendingDelete(report);
  };

  const handleDeleteReport = async (report) => {
    if (!report || deletingId || detailLoading) return;

    try {
      setDeletingId(report.id);
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`/api/internal/readiness-score/deleteConsolidatedReport/${report.id}/`),
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      setConsolidatedHistory((prev) => prev.filter((r) => r.id !== report.id));
      setStatusModal({
        open: true,
        type: "success",
        message: t("deletedConsolidatedReport"),
      });
    } catch (err) {
      console.error("Error deleting consolidated report:", err);
      setStatusModal({
        open: true,
        type: "error",
        message: err.message || t("failedToDeleteConsolidatedReport"),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const consolidatedSearchLower = consolidatedSearchQuery.trim().toLowerCase();
  const filteredConsolidatedHistory = consolidatedHistory.filter((report) => {
    if (consolidatedProfileTypeFilter && getProfileType(report) !== consolidatedProfileTypeFilter) {
      return false;
    }
    if (!consolidatedSearchLower) return true;
    const id = String(report.id ?? "").toLowerCase();
    const name = (report.report_name || "").toLowerCase();
    return (
      id.includes(consolidatedSearchLower) ||
      name.includes(consolidatedSearchLower)
    );
  });

  const sortedConsolidatedHistory = [...filteredConsolidatedHistory].sort((a, b) =>
    sortReports(a, b, historySortBy, historySortAsc)
  );

  return (
    <>
      <ConfirmModal
        open={pendingDelete != null}
        title={t("deleteReport")}
        message={
          pendingDelete
            ? `${t("delete")} "${pendingDelete.report_name || `${t("report")} ${pendingDelete.id}`}"? ${t("thisActionCannotBeUndone")}`
            : ""
        }
        confirmLabel={t("delete")}
        cancelLabel={t("cancel")}
        zIndex={2100}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const report = pendingDelete;
          setPendingDelete(null);
          if (report) handleDeleteReport(report);
        }}
      />
      <StatusResultModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        zIndex={2100}
        onClose={() =>
          setStatusModal({ open: false, type: "success", message: "" })
        }
      />
      <div className="rh-search-container">
        <div className="target-select-row" style={{ marginBottom: 0 }}>
          <label htmlFor="consolidated-profile-type-filter">{t("profileType")}</label>
          <select
            id="consolidated-profile-type-filter"
            value={consolidatedProfileTypeFilter}
            onChange={(e) => setConsolidatedProfileTypeFilter(e.target.value)}
            disabled={loadingHistory}
          >
            <option value="">{t("all")}</option>
            {PROFILE_TYPE_FILTER_OPTIONS.map((type) => (
              <option key={`consolidated-${type}`} value={type}>
                {profileTypeLabels[type] || type}
              </option>
            ))}
          </select>
        </div>
        <input
          type="text"
          className="rh-search-input"
          placeholder={t("searchByIdOrName")}
          value={consolidatedSearchQuery}
          onChange={(e) => setConsolidatedSearchQuery(e.target.value)}
        />
      </div>

      <div className="rh-profile-table rh-profile-table--consolidated-history">
        <div className="rh-profile-table-header">
          {[
            ["id", t("id"), "rh-profile-table-cell-report"],
            ["report_name", t("name"), "rh-profile-table-cell-name"],
            ["created_at", t("createdAt"), "rh-profile-table-cell-time"],
            ["overall_score", t("overallScore"), "rh-profile-table-cell-score"],
            ["profile_type", t("profileType"), "rh-profile-table-cell-profile-type"],
          ].map(([key, label, cls]) => (
            <div
              key={key}
              className={`rh-profile-table-cell ${cls}`}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (historySortBy === key) setHistorySortAsc(!historySortAsc);
                else {
                  setHistorySortBy(key);
                  setHistorySortAsc(true);
                }
              }}
            >
              {label}{historySortBy === key ? (historySortAsc ? " ▲" : " ▼") : ""}
            </div>
          ))}
          <div className="rh-profile-table-cell rh-profile-table-cell-actions">
            {t("actions")}
          </div>
        </div>
        <div className="rh-profile-table-body">
          {loadingHistory ? (
            <div className="rh-profile-table-row">
              <div className="rh-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                {t("loadingConsolidatedReports")}
              </div>
            </div>
          ) : filteredConsolidatedHistory.length === 0 ? (
            <div className="rh-profile-table-row">
              <div className="rh-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                {consolidatedHistory.length === 0
                  ? t("noConsolidatedReportsFound")
                  : consolidatedSearchQuery.trim()
                    ? t("noReportsMatchSearch")
                    : t("noReportsMatchProfileType")}
              </div>
            </div>
          ) : (
            sortedConsolidatedHistory.map((report, index) => {
              const score = calculateOverallScore(report);
              return (
                <div
                  key={report.id || index}
                  className="rh-profile-table-row"
                  onClick={() => !detailLoading && !deletingId && handleConsolidatedRowClick(report)}
                >
                  <div className="rh-profile-table-cell rh-profile-table-cell-report">
                    {report.id || t("na")}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-name">
                    <span title={report.report_name || t("na")}>{report.report_name || t("na")}</span>
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-time">
                    {formatDate(report.created_at || new Date().toISOString())}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-score">
                    <div className="readiness-bar-container">
                      <span className="readiness-percentage">{score}%</span>
                      <div className="readiness-bar">
                        <div
                          className={`readiness-fill readiness-${getReadinessLevel(score)}`}
                          style={{ width: `${score}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-profile-type">
                    {getProfileTypeLabel(report) || t("na")}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-actions">
                    <button
                      type="button"
                      className="rh-delete-btn"
                      title={t("deleteReport")}
                      disabled={deletingId === report.id || detailLoading || loadingHistory}
                      onClick={(e) => requestDeleteReport(report, e)}
                    >
                      {deletingId === report.id ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                      ) : (
                        <i className="fas fa-trash" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
